import { spliceBlocks, deleteRange, insertBlockAfter, insertChars, replaceBlock } from './ops'
import type { Block, Blocks } from '@w/content-blocks/src/block'
import type { CoreState } from './state'
import { validateLink } from '@w/md/src/parser/utils/link'
import { formatBlock } from './format-block'
import { inInlineSlot } from './levels'
import { children, clean, locate, plain, pointAt, rows, sliceInline, leaf, type Row } from './tree'

const punctuation = /[\s!"#$%&'()*+,./:;<=>?@[\\\]^_`{|}~-]/
const matchers = [
  { tag: '`', bit: 16 },
  { tag: '$', bit: 0 },
  { tag: '**', bit: 1 },
  { tag: '__', bit: 1 },
  { tag: '*', bit: 2 },
  { tag: '_', bit: 2 },
  { tag: '~~', bit: 4 },
  { tag: '~', bit: 4 },
]

/**
 * Every rule below matches text that ends at the caret: a block prefix ends in ' ' (or '—-'), an autolink in ' ',
 * the arrow in '>', an inline matcher in its closing tag. So after typing a grapheme that does not end in one of these characters,
 * `shortcuts` returns false; bulk insertion checks only after graphemes that pass this test (apply-intent.ts textRuns).
 */
const shortcutEnds = new Set([' ', '-', '>', ...matchers.map(({ tag }) => tag.at(-1))])
export function mayEndShortcut(grapheme: string): boolean {
  return shortcutEnds.has(grapheme.at(-1))
}

/** Transforms are separate history commits, so undo restores literal delimiters. */
export function shortcuts(state: CoreState): boolean {
  if (!state.selection) return false
  const entries = rows(state.blocks)
  const position = locate(entries, state.selection.anchor)
  const row = entries[position.row]
  if (blockShortcut(state, row, position.offset)) return true
  if (row.block.type === 'pre' || row.block.type === 'code') return false
  const point = state.selection.anchor
  const find = (items: Blocks): Blocks | undefined => {
    if (items.some((block) => block.id === point.blockId)) return items
    for (const block of items) {
      const found = find(children(block))
      if (found) return found
    }
    return undefined
  }
  const siblings = find(row.block.children)
  const closeNode = siblings?.find((block) => block.id === point.blockId)
  if (!siblings || closeNode?.type !== 'chars' || (closeNode.format ?? 0) & 16) return false
  const closeIndex = siblings.indexOf(closeNode)
  let from = closeIndex
  while (from > 0) {
    const previous = siblings[from - 1]
    if (previous.type === 'br' ||
      (previous.type === 'chars' && (previous.format ?? 0) & 16)) break
    from -= 1
  }
  const group = siblings.slice(from, closeIndex + 1)
  let total = 0
  const spans = group.map((block) => {
    const start = total
    total += plain(block).length
    return { block, start, end: total }
  })
  const text = group.map((block) => plain(block)).join('')
  const offset = text.length - plain(closeNode).length + point.offset
  // A typed `[label](url)` stays literal (superseded 2026-11-01 by Notion browser check imp24: `[site](https://a.com)`
  // then space keeps the full syntax; BlockSuite's inline link markdown is not Notion's).
  // A URL followed by a typed space becomes a link to itself (BlockNote autolink, Notion link on paste and type).
  const url = siblings === row.block.children ? /(?:^|\s)(https?:\/\/[^\s<>]+) $/u.exec(text.slice(0, offset)) : null
  if (url && validateLink(url[1]) && spans.every(span => span.block.type === 'chars')) {
    const start = offset - 1 - url[1].length
    spliceBlocks(state, siblings, from, group.length,
      ...sliceInline(group, 0, start, false, state),
      { id: '', type: 'a', href: url[1], children: sliceInline(group, start, offset - 1, true, state) },
      ...sliceInline(group, offset - 1, Infinity, true, state))
    clean(state, state.blocks)
    const next = pointAt(row, position.offset)
    state.selection = { anchor: next, focus: next }
    return true
  }
  if (text.slice(offset - 2, offset) === '->') {
    deleteRange(state, row.block.children, position.offset - 2, position.offset)
    if (!insertChars(state, row.block.children, position.offset - 2, '→', closeNode.format ?? 0)) {
      spliceBlocks(state, row.block.children, 0, 0, leaf('→', closeNode.format ?? 0))
    }
    const next = pointAt(row, position.offset - 1)
    state.selection = { anchor: next, focus: next }
    return true
  }
  for (const { tag, bit } of matchers) {
    const close = offset - tag.length
    if (text.slice(close, offset) !== tag || text[close - 1] === ' ') continue
    if (tag === '_' && text[offset] && !punctuation.test(text[offset])) continue
    let open = -1
    for (let at = close - tag.length; at >= 0; at -= 1) {
      const span = spans.find((span) => span.start <= at && at + tag.length <= span.end)
      if (span?.block.type !== 'chars') continue
      if (text.slice(at, at + tag.length) === tag &&
        (tag === '`' || text[at + tag.length] !== ' ')) {
        open = at
        break
      }
    }
    if (open < 0 || open + tag.length === close || text[open + tag.length] === ' ') continue
    const openSpan = spans.find((span) => span.start <= open && open < span.end)
    const beforeOpen = text.slice(openSpan?.start ?? 0, open)
    const beforeChar = beforeOpen.at(-1)
    if (beforeChar === tag.at(-1) && tag.length === 1) continue
    if (tag === '_' && beforeChar && !punctuation.test(beforeChar)) continue
    // A `**` or `~` opener right after a word character, in any formatting, stays literal (Notion browser checks imp05:
    // typing `aa**b**` leaves the asterisks literal; imp24: `aa~b~` leaves the tildes literal).
    if (tag === '**' && open > 0 && !punctuation.test(text[open - 1])) continue
    if (tag === '~' && open > 0 && !punctuation.test(text[open - 1])) continue
    if ((beforeOpen.match(/`/g)?.length ?? 0) % 2 !== 0) continue
    const middle = sliceInline(group, open + tag.length, close, open > 0, state)
    const clear = middle.every((block) => block.type === 'chars' && ((block.format ?? 0) & bit) !== 0)
    const format = (block: Block) => {
      if (block.type === 'chars') block.format = clear ? (block.format ?? 0) & ~bit : (block.format ?? 0) | bit
      children(block).forEach((child) => { format(child) })
    }
    middle.forEach((block) => { format(block) })
    spliceBlocks(state, siblings, from, group.length,
      ...sliceInline(group, 0, open, false, state), ...(tag === '$'
        ? [{ id: '', type: 'code' as const, latex: true as const,
          children: [{ id: '', type: 'chars' as const, text: [{ id: '', text: text.slice(open + 1, close) }] }] }] : middle),
      ...sliceInline(group, offset, Infinity, true, state))
    clean(state, state.blocks)
    const next = pointAt(row, position.offset - tag.length * 2)
    state.selection = { anchor: next, focus: next }
    state.pendingFormat ??= closeNode.format ?? 0
    return true
  }
  return false
}

/**
 * A wrapping shortcut (quote, list, toggle, hr) moves the row's runs into a new paragraph placed after the row before
 * the row is retyped: the runs keep their identity (the engine moves them, crdt_rs move_node), the retyped block holds
 * nothing its new type forbids, so a peer's concurrent typing in the runs survives (crdt_rs set_type N8).
 */
function wrapRuns(state: CoreState, row: Row, runs: Blocks): Block {
  const paragraph: Block = { id: '', type: 'p', children: runs }
  insertBlockAfter(state, row.siblings, row.block, paragraph)
  Reflect.set(row.block, 'children', [])
  return paragraph
}

function contains(block: Block, target: Block): boolean {
  return children(block).some(child => child === target || child.id === target.id || contains(child, target))
}

/** Markdown block syntax at a row's start turns the row into its block; in an inline-only slot it stays text (INV-LEVEL). */
function blockShortcut(state: CoreState, row: Row, offset: number): boolean {
  const item = row.parents.at(-1)
  const list = row.parents.at(-2)
  const itemRow = item?.type === 'li' && item.children[0] === row.block && list?.type === 'list' && list.subtype !== 'toggle'
  if (inInlineSlot(state, row) || !['p', 'h'].includes(row.block.type) ||
    (row.parents.some((parent) => parent.type === 'li') && !itemRow)) return false
  const first = row.block.children[0]
  if (first?.type !== 'chars' || offset > plain(first).length) return false
  const prefix = plain(first).slice(0, offset)
  const listPrefix = prefix.trimStart()
  // imp19: indentation before a bullet marker stays literal instead of starting a nested bullet.
  if (prefix !== listPrefix && /^[*+-] $/.test(listPrefix)) return false
  const marker = /^(?:[*+-]|(?:\d+|a|i)[.)]|-?\[\s?\]|-?\[x\]) $/i.test(listPrefix)
  const listKind = marker && listPrefix.includes('[') ? 'check' as const
    : marker && /^(?:\d|a|i)/i.test(listPrefix) ? 'number' as const : marker ? 'bullet' as const : undefined
  // A bullet prefix at the start of a heading, or a list prefix at the start of a list item of another kind, turns that
  // block into the list kind like turn into (Notion browser checks imp26: `- ` in H2 "abc" → bullet "abc"; imp06: `1. `
  // in to-do "abc" → numbered "abc", the prefix used up). Other prefixes in a heading are unchecked and stay literal.
  const itemSubtype = list?.type === 'list' ? list.subtype : undefined
  if (listKind && (itemRow || (row.block.type === 'h' && listKind === 'bullet')) &&
    (!itemRow || itemSubtype !== { bullet: undefined, number: 'ordered', check: 'todo' }[listKind])) {
    deleteRange(state, row.block.children, 0, offset)
    const point = pointAt(row, 0)
    state.selection = { anchor: point, focus: point }
    formatBlock(state, row, listKind)
    return true
  }
  if (itemRow) return false
  /** The marker is a text-range delete on the existing run; the surviving runs keep their identity. */
  const content = () => {
    deleteRange(state, row.block.children, 0, offset)
    return row.block.children.slice()
  }
  const id = row.block.id
  // Notion: three hyphens make a divider at once (columns-headings-and-dividers help); `___` still waits for space;
  // `***` then space stays literal (Notion browser check imp22).
  if (/^(?:---|___ |—-)$/.test(prefix)) {
    // The runs move into the new paragraph before the block turns into hr, so a peer's typing in them survives.
    const paragraph = wrapRuns(state, row, content())
    replaceBlock(state, row.block, { id: row.block.id, type: 'hr' })
    const point = pointAt(rows([paragraph])[0], 0)
    state.selection = { anchor: point, focus: point }
    return true
  }
  let next: Block
  if (/^#{1,3} $/.test(prefix)) {
    next = { id, type: 'h', level: (prefix.length - 1) as 1 | 2 | 3, children: content() }
  } else if (row.block.type !== 'p') return false
  // Notion's `" ` quote trigger, also as the typographic quote macOS substitutes for it (BlockNote quote input rule).
  else if (prefix === '" ' || prefix === '\u201C ') {
    next = { id, type: 'quote', children: [wrapRuns(state, row, content())] }
  } else if (prefix === '> ') {
    next = { id, type: 'list', subtype: 'toggle', children: [
      { id: '', type: 'li', children: [wrapRuns(state, row, content())] },
    ] }
  } else if (/^```[\w+-]* $/.test(prefix)) {
    const lang = prefix.slice(3, -1)
    next = { id, type: 'pre', ...(lang ? { lang } : {}), children: content() }
  }
  else if (listKind) {
    const check = listPrefix.includes('[')
    // `N.` and `N)` both start a numbered list (Lexical proposed parenthesis shortcut). `N.` starts it at N (superseded
    // 2026-10-30 by Notion browser check: `7. ` → `notion-numbered_list-block`, marker "7."; Lexical List start-number
    // markdown agrees); `N)` still numbers from 1 until Notion is checked for it (D6.4).
    const start = Number(/^(\d+)\./.exec(listPrefix)?.[1] ?? 0)
    next = { id, type: 'list',
      ...(check ? { subtype: 'todo' } : /^(?:\d|a|i)/i.test(listPrefix)
        ? { subtype: 'ordered', ...(start > 1 ? { start } : {}) } : {}),
      children: [{ id: '', type: 'li',
        ...(check ? { done: /^-?\[x\]/i.test(listPrefix) } : {}),
        children: [wrapRuns(state, row, content())] }],
    }
  } else return false
  replaceBlock(state, row.block, next)
  // The wrapped paragraph now lives inside `next`; drop its temporary sibling slot (the engine moved it).
  const parked = row.siblings.findIndex(block => block !== row.block && block !== next && contains(next, block))
  if (parked >= 0) row.siblings.splice(parked, 1)
  const nextRow = rows([next])[0]
  const point = pointAt(nextRow, 0)
  state.selection = { anchor: point, focus: point }
  return true
}
